<template>
  <div class="tool-call-block">
    <v-expansion-panels v-model="panelIndex" flat>
      <v-expansion-panel>
        <v-expansion-panel-title>
          <ToolCallTitle :status="headerStatus" :tool-name="toolName" :status-key="statusKey" />
          <template #actions>
            <BbBtn
              size="x-small"
              type="icon"
              :icon="panelIndex === 0 ? 'chevron-up' : 'chevron-down'"
              icon-size="small"
              no-background
            />
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ToolStepRow
            v-for="(step, i) in steps"
            :key="i"
            :events="step.events"
            :title="step.title"
            :decision="step.decision"
            :is-loading="step.isLoading"
            :format-content="formatStepContent"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { APPROVAL_DECISION, TOOL_CALL_TYPE, TOOL_STEP_STATUS } from '~/constants/conversationModels';

import { formatKey } from '~/utils/format';
import { getDecisionLabel } from '~/utils/step-label';

import ToolCallTitle from './ToolCallTitle.vue';
import ToolStepRow from './ToolStepRow.vue';

const stripTrailingBr = (html) => html.replace(/(<br\s*\/?>)\s*$/gi, '').trimEnd();

const getToolName = (call) => formatKey(call.data?.tool_name || '');

// index of the call's event that is still waiting on its tool-finished, or -1 once the call errored
const getActiveLoadingIndex = (events) =>
  events.reduce((runningIndex, event, index) => {
    const eventType = event?.data?.event;
    if (eventType === TOOL_CALL_TYPE.TOOL_ERROR) {
      return -1;
    }
    if (!eventType || eventType === TOOL_CALL_TYPE.TOOL_FINISHED) {
      return runningIndex;
    }
    return index;
  }, -1);

const isCallRunning = (call) => !call.done && getActiveLoadingIndex(call.events || []) !== -1;

export default {
  name: 'ToolCallBlockSSE',
  components: { ToolCallTitle, ToolStepRow },
  props: {
    calls: { type: Array, default: () => [] },
    interrupt: { type: Object, default: null },
    sanitizeAndParse: { type: Function, required: true },
    isTyping: { type: Boolean, default: false },
    followedByText: { type: Boolean, default: false },
    awaitingApproval: { type: Boolean, default: false },
  },
  setup(props) {
    const { t } = useI18n();
    const panelIndex = ref(null);

    const done = computed(() => props.calls.every((call) => call.done));

    const isComplete = computed(
      () => done.value && !props.awaitingApproval && (props.followedByText || !props.isTyping),
    );

    const conclusion = computed(() => {
      const lastStep = props.calls[props.calls.length - 1];
      if (lastStep?.decision) {
        return {
          status: lastStep.decision === APPROVAL_DECISION.REJECT ? TOOL_STEP_STATUS.ERROR : TOOL_STEP_STATUS.DONE,
          label: getDecisionLabel(lastStep.decision, t),
        };
      }
      if ((lastStep?.events || []).some((e) => e?.data?.event === TOOL_CALL_TYPE.TOOL_ERROR)) {
        return { status: TOOL_STEP_STATUS.ERROR, label: t('common.failed') };
      }
      return { status: TOOL_STEP_STATUS.DONE, label: t('common.done') };
    });

    const headerStatus = computed(() => (isComplete.value ? conclusion.value.status : TOOL_STEP_STATUS.RUNNING));

    // the step the header names: the one running now, or between steps the one that ran last
    const currentCall = computed(() => {
      const runningCalls = props.calls.filter(isCallRunning);
      return runningCalls[runningCalls.length - 1] || props.calls[props.calls.length - 1];
    });

    const isWaitingForApproval = computed(() => props.awaitingApproval && !props.calls.some(isCallRunning));

    const toolName = computed(() => {
      if (isComplete.value) return conclusion.value.label;
      if (isWaitingForApproval.value) return t('conversation.toolSteps.waitingForApproval');
      return currentCall.value ? getToolName(currentCall.value) : '';
    });

    const statusKey = computed(() =>
      isComplete.value || isWaitingForApproval.value
        ? toolName.value
        : currentCall.value?.data?.tool_call_id || toolName.value,
    );

    const steps = computed(() =>
      props.calls.map((call) => ({
        events: (call.events || []).filter((event) => event?.data),
        title: getToolName(call),
        decision: call.decision || '',
        isLoading:
          !call.decision &&
          (isCallRunning(call) || (!isComplete.value && !isWaitingForApproval.value && call === currentCall.value)),
      })),
    );

    const formatStepContent = (content) => stripTrailingBr(props.sanitizeAndParse(content));

    watch(isComplete, (complete) => {
      if (complete) panelIndex.value = null;
    });

    return { panelIndex, headerStatus, toolName, statusKey, steps, formatStepContent };
  },
};
</script>

<style scoped lang="scss">
.tool-call-block {
  width: 422px;
  max-width: 100%;

  :deep(.v-expansion-panel) {
    background-color: transparent;
    border-radius: 0;

    .v-expansion-panel__shadow {
      box-shadow: none;
    }

    .v-expansion-panel-title {
      height: 49px;
      min-height: 49px;
      padding: $bb-spacing-2 $bb-spacing-3;
      border-radius: 16px;
      background-color: $bb-neutral-6;
      color: $bb-neutral-1;
      transition: background-color 0.2s;

      &:hover,
      &:focus-visible {
        background-color: $bb-neutral-5;
      }
    }
    .v-expansion-panel-title__overlay {
      display: none;
    }
    .v-expansion-panel-title__icon .bb-btn.icon:hover {
      background-color: transparent !important;
    }

    .v-expansion-panel-text .v-expansion-panel-text__wrapper {
      display: flex;
      flex-direction: column;
      gap: $bb-spacing-1;
      margin-top: $bb-spacing-1;
      padding: $bb-spacing-4 $bb-spacing-5;
      max-height: 40vh;
      overflow-y: auto;
      background-color: $bb-neutral-0;
      border: 1px solid #cecdcc;
      border-radius: 16px;

      > * {
        flex-shrink: 0;
      }
    }
  }
}
</style>
